import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import * as moment from 'moment-timezone';
import { FasttrackPromotionLogs } from '@core/models/fasttrack-promotion-logs.model';
import { FasttrackPromotionLogsDataService } from './services/fasttrack-promotion-logs-data.service';
import { AuthHttpService } from '@core/services/auth-http.service';
import { TransactionHttpService } from '@core/services/transaction-http.service';
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { AppPermissionService } from '@core/services/app-permission.service';
import { Observable, of, Subject, Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { MatDialog } from '@angular/material/dialog';
import { MemberDataService } from '../../general/members/services/member-data.service';
import { MemberInformationDialogComponent } from '../../general/members/dialogs/member-information/member-information.component';
import { PromotionSettingHttpService } from '@core/services/promotion-setting-http.service';
import { Promotion } from '@core/models/promotion.model';
import { Pagination } from '@core/models/pagination.model';
import { LoadingBarService } from "@ngx-loading-bar/core";
import { DropdownHttpService } from '@core/services/dropdown-http.service';

@Component({
  selector: 'kt-fasttrack-promotion-logs',
  templateUrl: './fasttrack-promotion-logs.component.html',
  styleUrls: ['./fasttrack-promotion-logs.component.scss']
})
export class FasttrackPromotionLogsComponent implements OnInit {
  form: FormGroup;
  ranges: any;
  maxDate: any;
  dateTimePickerLocale: any;

  page = 1;
  pageSize = 30;
  params = '';
  pagination: Pagination;
  loading = false;
  clearBtnLoading = false;
  searchBtnLoading = false;
  dataLength: number;
  timezone = JSON.parse(localStorage.getItem('user_data')).timezone;

  promotions = [];
  logs$: Observable<FasttrackPromotionLogs[]>;
  messages$ = this.fasttrackPromotionLogsDataService.messages$;
  messages2$ = new Subject<any[]>();

  sortingConfig = {
    'id': 'desc',
    'username': 'desc',
    'promo_code': 'desc',
    'fast_track_id': 'desc',
    'created_at': 'desc',
  };
  sortingSelection = {
    'sort_by': 'id',
    'sort_order': 'desc',
  };
  dropdown = {
    perPage: this.dropdownHttpService.perPage
  };

  // permissions
  canViewFasttrackPromotionLogs: boolean;
  canViewMemberDialog: boolean;
  canAccessLinkedMemberPromo: boolean;
  canAccessLinkedMemberMessage: boolean;
  canAccessLinkedMemberSms: boolean;

  private memberinfo_subscription = new Subscription();
  private permission_subscription = new Subscription();

  constructor(
    public dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private authHttpService: AuthHttpService,
    private transactionHttpService: TransactionHttpService,
    private fasttrackPromotionLogsDataService: FasttrackPromotionLogsDataService,
    private appPermissionService: AppPermissionService,
    private memberDataService: MemberDataService,
    private promotionSettingHttpService: PromotionSettingHttpService,
    private loadingBar: LoadingBarService,
    private dropdownHttpService: DropdownHttpService,
  ) { }

  ngOnInit(): void {
    this.ranges = this.transactionHttpService.ranges;
    this.dateTimePickerLocale = this.transactionHttpService.dateTimePickerLocale;

    this.formInit();

    const apSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      this.canViewFasttrackPromotionLogs = appPermissions.view_crm_fasttrack_promotion_log;
      this.canViewMemberDialog = appPermissions.crm_fasttrack_view_member_dialog;
      this.canAccessLinkedMemberPromo = appPermissions.crm_fasttrack_link_member_promo;
      this.canAccessLinkedMemberMessage = appPermissions.crm_fasttrack_link_member_message;
      this.canAccessLinkedMemberSms = appPermissions.crm_fasttrack_link_member_sms;
      this.cdr.detectChanges();
    });

    this.permission_subscription.add(apSub);

    this.promotionSettingHttpService.getAllPromoSettings()
      .subscribe((res: any) => this.promotions = res.promotions);
  }

  ngOnDestroy() {
    this.memberinfo_subscription.unsubscribe();
    this.permission_subscription.unsubscribe();
  }

  onClear() {
    this.formInit();
    this.onSubmit();
  }

  toLowerCaseInput(controlName: string, event: Event) {
    this.authHttpService.forceLowerCaseInputControl(this.form, controlName, event);
  }

  onClearDate() {
    if (this.form.value.start_date !== null) {
      this.form.patchValue({ defaultDate: null });
    }
  }

  onDateRange(event: any) {
    if (event) {
      this.form.patchValue({
        start_date: event.startDate !== null && event.startDate !== undefined ? event.startDate : null,
        end_date: event.endDate !== null && event.endDate !== undefined ? event.endDate : null
      });
    }
  }

  updateDateRange() {
    this.ranges = this.transactionHttpService.updateDateRange();
  }

  onSubmit(clearSearch?: boolean) {
    this.timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
    this.searchBtnLoading = clearSearch ? false : true;
    const formValue = this.form.value;
    if (formValue.promotion) {
      if (formValue.promotion.code != undefined && formValue.promotion.name != undefined) {
        formValue.code = formValue.promotion.code;
        formValue.promotion_name = formValue.promotion.name;
        delete formValue.promotion;
      }
    }
    of(this.form.value).pipe(
      map(this.filterFormFields),
      tap((data) => {
        console.log(data);
        if (data['start_date']) {
          data['start_date'] = moment(data['start_date']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
          data['end_date'] = moment(data['end_date']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
        }

        this.params = Object.keys(data).map(key => key + '=' + data[key]).join('&');
        this.page = 1;
        this.onViewPageBy();
      })
    ).subscribe();
  }

  onViewPageBy(page = 1, pageSize?: number, params?: string) {
    this.loading = true;
    pageSize = this.pageSize;
    params = this.params ? `&${this.params}` : '';
    this.loadingBar.start();
    return this.logs$ = this.fasttrackPromotionLogsDataService.getWithQuery(`page=${page}&perPage=${pageSize}&${this.generateSortingParam()}${params}`).pipe(
      tap(res => {
        this.loading = false;
        this.clearBtnLoading = false;
        this.searchBtnLoading = false;
        this.dataLength = res.length;
        this.pagination = this.fasttrackPromotionLogsDataService.pagination;
        this.loadingBar.complete();
      })
    );
  }

  private generateSortingParam() {
    const sortingParams = Object.keys(this.sortingSelection).map(key => key + '=' + this.sortingSelection[key]).join('&');
    return sortingParams;
  }

  private formInit() {
    let defaultStart = this.transactionHttpService.getToday().from;
    let defaultEnd = this.transactionHttpService.getToday().to;

    this.form = new FormGroup({
      username: new FormControl(null),
      fast_track_id: new FormControl(null),
      promotion_code: new FormControl(null),
      start_date: new FormControl(defaultStart),
      end_date: new FormControl(defaultEnd),
      defaultDate: new FormControl({
        startDate: defaultStart,
        endDate: defaultEnd,
      }), // Do not remove: For Clearing The Range
    });
  }

  private filterFormFields(formData: any) {
    const fields = {};
    Object.keys(formData).forEach(key => {
      if (formData[key] && formData[key] !== 'all' && key !== 'defaultDate') {
        fields[key] = formData[key];
      }
    });
    return fields;
  }

  onSortColumn(property: string) {
    // Reset other columns
    for (const key in this.sortingConfig) {
      if (Object.prototype.hasOwnProperty.call(this.sortingConfig, key)) {
        if (key == property) {
          this.sortingConfig[key] = this.sortingConfig[key] === 'asc' ? 'desc' : 'asc';
        } else {
          this.sortingConfig[key] = 'desc';
        }
      }
    }
    // User selection
    this.sortingSelection.sort_by = property;

    if (this.sortingSelection.sort_by === property) {
      // Same column
      this.sortingSelection.sort_order = this.sortingConfig[property];
    } else {
      // Switch to other column
      this.sortingConfig[property] = 'asc';
      this.sortingSelection.sort_order = 'asc';
    }
    // Load Data
    this.onViewPageBy(this.page, this.pageSize, this.params);
  }

  showMember(memberId: number) {
    if (!this.canViewMemberDialog) {
      Swal.fire({
        icon: 'error',
        title: 'Permission Denied',
        text: 'You do not have permission to view member information.',
      });
      return;
    }

    let start_date = moment(this.transactionHttpService.getYesterday().from).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
    let end_date = moment(this.transactionHttpService.getLast24Hours().to).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');

    const member = this.memberDataService.getById(memberId, `?start_date=${start_date}&end_date=${end_date}&additional_info=1`);

    this.memberinfo_subscription = member.pipe(
      tap((res) => {
        if (res) {
          let data = { member: res, mode: 'member-information' };

          this.dialog.open(MemberInformationDialogComponent, {
            width: '1500px',
            height: '80vh',
            data,
            autoFocus: false,
          });
        }
      })
    ).subscribe();
  }

  formatPromo = (p: Promotion | string | null) =>
    !p || typeof p === 'string' ? (p || '') : `${p.code} - ${p.name}`;

  searchPromo = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term => {
        const t = (term || '').toLowerCase();
        const list = this.promotions || [];
        return t
          ? list.filter(p => (`${p.code} ${p.name}`).toLowerCase().includes(t)).slice(0, 10)
          : list.slice(0, 10);
      })
    );

  onPromoSelected(p: Promotion) {
    this.form.patchValue({ promotion_code: p.code }); // keep the object in the input
  }

  linkPromotion(promotion_id: number) {
    const autoSearchKey = 'promotion_auto_search_by_id';
    localStorage.setItem(autoSearchKey, promotion_id.toString());
    // redirect to 3.2
    window.open('/general/promotion-codes', '_blank');
  }

  linkMessage(username: string) {
    const autoSearchKey = 'message_auto_search_by_username';
    localStorage.setItem(autoSearchKey, username);
    // redirect to 6.1
    window.open('/sms/all-messages', '_blank');
  }

  linkSms(username: string) {
    const autoSearchKey = 'sms_auto_search_by_username';
    localStorage.setItem(autoSearchKey, username);
    // redirect to 6.2
    window.open('/sms/logs', '_blank');
  }
}
